<script setup lang="ts">
import { ref, onMounted, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'

import { CategoryFull } from '~/js/interfaces/facets'
import { getClasses } from '~/js/composables/classes'
import { getGlobalTranslation } from '~/js/composables/translations'
import { useTeambuildingState } from '~/js/stores/teambuildings-tm'

import TextH5 from '~/vue/_teammasters/atoms/texts/TextH5.vue'
import TextProse from '~/vue/_teammasters/atoms/texts/TextProse.vue'

interface Props {
    description?: string|null
    label: string
    multiSelect?: boolean
    options: Array<CategoryFull|null>|null
    type: string|null|undefined
    utilities?: string
}

/** 
 * VARIABLES
 * **/
const props = withDefaults(defineProps<Props>(),{
    multiSelect: true, 
    utilities: '',
    description: null,
})
const emit = defineEmits(['input'])

// store
const teambuildingStore = useTeambuildingState()
const { filters } = storeToRefs(teambuildingStore)

// variables
const elOptions = ref<HTMLElement | null>(null)
const activeIndex = ref<number>(-1)
const selected = ref<Array<string|null>|null>([])

const classes = {
    wrapper: {
        _default: 'relative',
        color: 'bg-white',
        rounded: 'rounded-sm',
        focus: 'focus-within:ring-3 focus-within:ring-yellow',
    },
    list: {
        color: 'bg-white',
        size: 'w-full',
        spacing: 'mt-2 space-y-6 lg:space-y-4',
        display: ''
    },
    listItem: {
        _default: 'cursor-pointer',
        flex: 'flex items-center',
        spacing: 'gap-2',
        border: 'border border-transparent',
        rounded: 'rounded-lg',
        focusVisible: 'focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none focus-visible:ring-indigo',
        focus: 'focus:ring-2 focus:ring-offset-white dark:ring-offset-black focus:ring-offset-2 focus:outline-none focus:ring-indigo'
    },
    inputRadio: {
        _default: 'relative shrink-0',
        radius: props.multiSelect ? 'rounded-sm' : 'rounded-full',
        border: 'border border-black',
        size: 'w-4 lg:w-5 h-4 lg:h-5',
    },
    inputRadioSelected: {
        _default: props.multiSelect ? 'after:rounded-xs lg:after:rounded-sm' : 'after:rounded-full',
        position: 'after:absolute after:left-1/2 after:top-1/2',
        transform: 'after:-translate-x-1/2 after:-translate-y-1/2',
        color: 'after:bg-black',
        size: 'after:w-2.5 after:lg:w-3.5 after:h-2.5 after:lg:h-3.5',
    },
}

/** 
 * METHODS
 * **/
const onSelect = (option:CategoryFull|null) => {
    if (!option || !Array.isArray(selected.value)) {
        return
    }
    
    if (selected.value.includes(option.id)) {
        selected.value.splice(selected.value.indexOf(option.id), 1)
    } else {
        selected.value.push(option.id)
    }

    emit('input', {type: props.type, values: selected.value})
}

const onHandleKeystroke = event => {
    switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown': {
                event.preventDefault()
                activeIndex.value = (activeIndex.value + 1) % (props.options?.length ?? 0)
                elOptions.value?.[activeIndex?.value]?.focus()
                break
            }
            case 'ArrowLeft':
            case 'ArrowUp': {
                event.preventDefault()
                activeIndex.value = (activeIndex.value - 1 + (props.options?.length ?? 0)) % (props.options?.length ?? 0)
                elOptions.value?.[activeIndex?.value]?.focus()
                break
            }
            case 'Enter': {
                event.preventDefault()
                const active:Element|null = document.activeElement
                if (active && active?.getAttribute('data-option')) {
                    let jsonString:string|null = active?.getAttribute('data-option')
                    const data:CategoryFull|null = JSON.parse(jsonString ?? '')
                    onSelect(data)
                }
                break
            }
    }
}

const isChecked = (option: CategoryFull | null):boolean => {
    if (!option) {
        return false
    }

    if (selected.value) {
        return selected.value.indexOf(option.id) > -1
    }

    return false
}


const setCurrent = () => {
    if (!props.type) {
        return 
    }

    const current = filters?.value[props.type]

    if (!current) {
        return
    }

    selected.value = current?.values
}

/**
 * WACHTERS
 **/
watchEffect(() => {
    setCurrent()
})

/** 
 * LIFECYCLES
 **/
onMounted(() => {
    setCurrent()
})
</script>

<template>
    <fieldset 
        :class="[
            getClasses(classes, 'wrapper'),
            utilities
        ]" 
    >
        <legend class="sr-only">{{ getGlobalTranslation('Set facet filtering for') }} {{ label }}"></legend>

        <label :for="`list-${label}`">
            <span :id="`listbox-label-${label}`">
                <TextH5 
                    size="text-base"
                    utilities="block mb-4"
                >
                    {{ label }}
                </TextH5>
            </span>

            <TextProse 
                v-if="description"
                size="text-sm"
                color="text-neutral-900 hover:text-black"
            >
                {{ description }}
            </TextProse>

            <ul
                :class="getClasses(classes, 'list')"
                :aria-labelledby="`listbox-label-${label}`"
                role="listbox"
                :id="`list-${label}`"
                tabindex="-1"
            >
                <template 
                    v-for="option in options"
                    :key="option?.id"
                >
                    <li
                        v-if="option"
                        :aria-labelledby="`option-${option.id}-label`"
                        :class="getClasses(classes, 'listItem')"
                        :data-option="JSON.stringify(option)"
                        :key="option.id"
                        @click.prevent="onSelect(option)"
                        @keydown="onHandleKeystroke"
                        ref="elOptions"
                        role="option"
                        tabindex="0"
                    >
                        <span 
                            :class="[
                                getClasses(classes, 'inputRadio'), 
                                isChecked(option) ? getClasses(classes, 'inputRadioSelected') : null
                            ]" 
                        />

                        <span :id="`option-${option.id}-label`">
                            <TextProse color="text-neutral-700 hover:text-black">{{ option.title }}</TextProse>
                        </span>
                    </li>
                </template>
            </ul>
        </label>
    </fieldset>
</template>